import React, { useEffect, useMemo, useState } from "react";
import { Box, Typography, IconButton, Tooltip, Button } from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import ShareIcon from "@mui/icons-material/Share";
import { generateReferral } from "../../api/referralService";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import theme from "../../theme/Theme";
import { useHookstate } from "@hookstate/core";
import { metaMaskState } from "../../hooks/store";

const ReferralsPage = () => {
  const [referralLinks, setReferralLinks] = useState("");
  const metamaskDataState = useHookstate(metaMaskState);
  useEffect(() => {
    if (metamaskDataState.get().isConnected) {
      setReferralLinks(
        `${window.location.origin}?referral_code=${
          metamaskDataState.get().address
        }`
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [metamaskDataState.get().isConnected]);

  const handleCopy = () => {
    if (referralLinks) {
      navigator.clipboard.writeText(referralLinks).then(() => {
        toast.success("Referral link copied to clipboard!", {
          style: { fontSize: "14px" },
        });
      });
    } else {
      toast.error("Referral link is not available at the moment.", {
        style: { fontSize: "14px" },
      });
    }
  };

  const handleShare = async () => {
    if (navigator.share) {
      try {
        const result = await navigator.share({
          title: "Referral Link",
          text: "Check this out!",
          url: referralLinks,
        });
        if (result) {
          toast.success("Referral link shared successfully!", {
            style: { fontSize: "14px" },
          });
        }
      } catch (error) {
        toast.error("Failed to share the referral link.", {
          style: { fontSize: "14px" },
        });
      }
    } else {
      toast.error("Sharing is not supported on this device.", {
        style: { fontSize: "14px" },
      });
    }
  };

  return (
    <Box sx={styles.container}>
      <Box sx={styles.pageBgImage} />
      <Box sx={styles.box}>
        <Typography variant="h5" sx={styles.header}>
          Invite Your Friends and Earn Rewards
        </Typography>
        <Typography variant="body2" sx={styles.subHeader}>
          Share your referral link with your friends and enjoy exclusive
          benefits when they join!
        </Typography>
        <Box sx={styles.linkBox}>
          {referralLinks ? (
            <>
              <Typography sx={styles.referralText}>
                <span style={styles.link}>{referralLinks}</span>
              </Typography>
              <Tooltip title="Copy to clipboard" arrow>
                <IconButton sx={styles.copyButton} onClick={handleCopy}>
                  <ContentCopyIcon />
                </IconButton>
              </Tooltip>
            </>
          ) : (
            <Typography sx={styles.noLinkText}>
              No referral link available. Please connect to your wallet and try
              again!
            </Typography>
          )}
        </Box>
        {!referralLinks && (
          <Typography
            variant="body2"
            sx={{ color: theme.palette.primary.main, fontWeight: "bold" }}
          >
            Click on Connect Wallet to get the Referral Link
          </Typography>
        )}
        {referralLinks && (
          <Button
            variant="contained"
            startIcon={<ShareIcon />}
            sx={styles.shareButton}
            onClick={handleShare}
            m={2}
          >
            Share Link
          </Button>
        )}
      </Box>
    </Box>
  );
};

export default ReferralsPage;

const styles = {
  container: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    position: "relative",
  },
  pageBgImage: {
    position: "absolute",
    backgroundColor: "#fcfcfe",
    backgroundImage:
      "url(\"data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M54.627 0l.83.828-1.415 1.415L51.8 0h2.827zM5.373 0l-.83.828L5.96 2.243 8.2 0H5.374zM48.97 0l3.657 3.657-1.414 1.414L46.143 0h2.828zM11.03 0L7.372 3.657 8.787 5.07 13.857 0H11.03zm32.284 0L49.8 6.485 48.384 7.9l-7.9-7.9h2.83zM16.686 0L10.2 6.485 11.616 7.9l7.9-7.9h-2.83zm20.97 0l9.315 9.314-1.414 1.414L34.828 0h2.83zM22.344 0L13.03 9.314l1.414 1.414L25.172 0h-2.83zM32 0l12.142 12.142-1.414 1.414L30 .828 17.272 13.556l-1.414-1.414L28 0h4zM.284 0l28 28-1.414 1.414L0 2.544V0h.284zM0 5.373l25.456 25.455-1.414 1.415L0 8.2V5.374zm0 5.656l22.627 22.627-1.414 1.414L0 13.86v-2.83zm0 5.656l19.8 19.8-1.415 1.413L0 19.514v-2.83zm0 5.657l16.97 16.97-1.414 1.415L0 25.172v-2.83zM0 28l14.142 14.142-1.414 1.414L0 30.828V28zm0 5.657L11.314 44.97 9.9 46.386l-9.9-9.9v-2.828zm0 5.657L8.485 47.8 7.07 49.212 0 42.143v-2.83zm0 5.657l5.657 5.657-1.414 1.415L0 47.8v-2.83zm0 5.657l2.828 2.83-1.414 1.413L0 53.456v-2.83zM54.627 60L30 35.373 5.373 60H8.2L30 38.2 51.8 60h2.827zm-5.656 0L30 41.03 11.03 60h2.828L30 43.858 46.142 60h2.83zm-5.656 0L30 46.686 16.686 60h2.83L30 49.515 40.485 60h2.83zm-5.657 0L30 52.343 22.343 60h2.83L30 55.172 34.828 60h2.83zM32 60l-2-2-2 2h4zM59.716 0l-28 28 1.414 1.414L60 2.544V0h-.284zM60 5.373L34.544 30.828l1.414 1.415L60 8.2V5.374zm0 5.656L37.373 33.656l1.414 1.414L60 13.86v-2.83zm0 5.656l-19.8 19.8 1.415 1.413L60 19.514v-2.83zm0 5.657l-16.97 16.97 1.414 1.415L60 25.172v-2.83zM60 28L45.858 42.142l1.414 1.414L60 30.828V28zm0 5.657L48.686 44.97l1.415 1.415 9.9-9.9v-2.828zm0 5.657L51.515 47.8l1.414 1.413 7.07-7.07v-2.83zm0 5.657l-5.657 5.657 1.414 1.415L60 47.8v-2.83zm0 5.657l-2.828 2.83 1.414 1.413L60 53.456v-2.83zM39.9 16.385l1.414-1.414L30 3.658 18.686 14.97l1.415 1.415 9.9-9.9 9.9 9.9zm-2.83 2.828l1.415-1.414L30 9.313 21.515 17.8l1.414 1.413 7.07-7.07 7.07 7.07zm-2.827 2.83l1.414-1.416L30 14.97l-5.657 5.657 1.414 1.415L30 17.8l4.243 4.242zm-2.83 2.827l1.415-1.414L30 20.626l-2.828 2.83 1.414 1.414L30 23.456l1.414 1.414zM56.87 59.414L58.284 58 30 29.716 1.716 58l1.414 1.414L30 32.544l26.87 26.87z' fill='%23f2f2f2' fill-opacity='0.4' fill-rule='evenodd'/%3E%3C/svg%3E\")",
    top: 0,
    left: 0,
    right: 0,
    zIndex: -1,
    width: "100vw",
    height: "180%",
  },
  box: {
    my: 3,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: 3,
    backgroundColor: theme.custom.white,
    boxShadow:
      "rgba(0, 0, 0, 0.1) 0px 4px 6px -1px, rgba(0, 0, 0, 0.06) 0px 2px 4px -1px",
    borderRadius: "8px",
    width: { xs: "90vw", lg: "60vw" },
  },
  header: {
    fontWeight: "bold",
    marginBottom: 1,
    color: theme.palette.text.primary,
    fontSize: 24,
    width: "fit-content",
  },
  subHeader: {
    color: theme.palette.text.secondary,
    marginBottom: 3,
    textAlign: "center",
    fontSize: 16,
    width: "fit-content",
  },
  linkBox: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    backgroundColor: theme.custom.white,
    border: "1px solid #ddd",
    borderRadius: "12px",
    padding: "8px 16px",
    width: "100%",
    maxWidth: "600px",
    textAlign: "center",
    marginBottom: 2,
    position: "relative",
  },
  referralText: {
    fontSize: "16px",
    color: theme.palette.text.disabled,
    fontWeight: "bold",
    flexGrow: 1,
    overflow: "hidden",
    whiteSpace: "nowrap",
    textOverflow: "ellipsis",
    textAlign: "left",
    marginRight: "8px",
    padding: "8px 0",
    width: "fit-content",
  },
  noLinkText: {
    fontSize: "14px",
    color: theme.palette.text.disabled,
    fontStyle: "italic",
  },
  link: {
    fontWeight: "600",
    textDecoration: "none",
    color: theme.custom.link,
  },
  copyButton: {
    color: theme.palette.primary.main,
    marginLeft: 1,
    backgroundColor: theme.custom.primaryLighter,
    borderRadius: "50%",
    "&:hover": {
      backgroundColor: "#e0f0ff",
    },
  },
  shareButton: {
    backgroundColor: theme.palette.primary.main,
    borderRadius: "4px",
    boxShadow: `${theme.palette.primary.light} 2px 2px, ${theme.custom.primaryLighter} 4px 4px`,
    "&:hover": {
      backgroundColor: theme.palette.primary.dark,
    },
  },
};
